import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslate } from "@tolgee/react";
import { CandlestickChart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { CountUp } from "./count-up";
import { LandingPrimaryCta } from "./landing-primary-cta.tsx";

import { useTradePerpMarkets } from "#/features/trade/hooks/use-trade-data";
import { getLocalizedMarketName } from "#/features/trade/lib/market-display";
import { formatPrice, formatSignedPercent } from "#/lib/formatters";
import { cn } from "#/lib/utils";

import type { TradePerpMarketSummary } from "#/features/trade/hooks/use-trade-data";

import { fadeBlurUp, scaleIn } from "#/components/ui/motion-primitives";
import { sectionClass } from "./landing-styles";

const phraseKeys = [
	{ routeSymbol: "xyz-SKHX", assetKey: "hero.asset-skhx", leverageKey: "hero.leverage-skhx", eyebrowKey: "hero.eyebrow-skhx" },
	{ routeSymbol: "xyz-SMSN", assetKey: "hero.asset-smsn", leverageKey: "hero.leverage-smsn", eyebrowKey: "hero.eyebrow-smsn" },
	{ routeSymbol: "xyz-NVDA", assetKey: "hero.asset-nvda", leverageKey: "hero.leverage-nvda", eyebrowKey: "hero.eyebrow-nvda" },
	{ routeSymbol: "BTC", assetKey: "hero.asset-btc", leverageKey: "hero.leverage-btc", eyebrowKey: "hero.eyebrow-btc" },
	{ routeSymbol: "xyz-GOLD", assetKey: "hero.asset-gold", leverageKey: "hero.leverage-gold", eyebrowKey: "hero.eyebrow-gold" },
	{ routeSymbol: "xyz-CL", assetKey: "hero.asset-wti", leverageKey: "hero.leverage-wti", eyebrowKey: "hero.eyebrow-wti" },
];

const TYPE_SPEED_MS = 68;
const BACK_SPEED_MS = 52;
const START_DELAY_MS = 2500;
const BACK_DELAY_MS = 2500;
const SWAP_DELAY_MS = 220;

export function HeroSection() {
	const { t } = useTranslate();
	const { markets } = useTradePerpMarkets();

	const heroPhrases = useMemo(
		() =>
			phraseKeys.map((pk) => ({
				routeSymbol: pk.routeSymbol,
				asset: t(pk.assetKey),
				leverage: t(pk.leverageKey),
				eyebrow: t(pk.eyebrowKey),
				assetLine: t("hero.typed-asset-line", { asset: t(pk.assetKey) }),
				leverageLine: t("hero.typed-leverage-line", { leverage: t(pk.leverageKey) }),
			})),
		[t],
	);

	const heroStats = useMemo(
		() => [
			{ value: "$3.2B+", label: t("hero.stat-volume"), highlight: true },
			{ value: "10x–40x", label: t("hero.stat-leverage") },
			{ value: "24/7", label: t("hero.stat-availability") },
		],
		[t],
	);

	const [phraseIndex, setPhraseIndex] = useState(0);
	const activePhrase = heroPhrases[phraseIndex];

	const longestAssetLine = useMemo(
		() => heroPhrases.reduce((a, b) => (b.assetLine.length > a.length ? b.assetLine : a), heroPhrases[0].assetLine),
		[heroPhrases],
	);
	const longestLeverageLine = useMemo(
		() => heroPhrases.reduce((a, b) => (b.leverageLine.length > a.length ? b.leverageLine : a), heroPhrases[0].leverageLine),
		[heroPhrases],
	);
	const longestEyebrow = useMemo(
		() => heroPhrases.reduce((a, b) => (b.eyebrow.length > a.length ? b.eyebrow : a), heroPhrases[0].eyebrow),
		[heroPhrases],
	);

	const assetTypedRef = useRef<HTMLSpanElement>(null);
	const leverageTypedRef = useRef<HTMLSpanElement>(null);
	const marketMap = new Map(markets.map((market) => [market.routeSymbol, market]));
	const boardMarkets = useMemo(() => phraseKeys
		.map((pk) => marketMap.get(pk.routeSymbol))
		.filter((market): market is TradePerpMarketSummary => market !== undefined)
		.slice(0, 6), [marketMap]);

	useEffect(() => {
		const assetEl = assetTypedRef.current;
		const leverageEl = leverageTypedRef.current;
		if (!assetEl || !leverageEl) return;

		const timeouts = new Set<number>();
		let cancelled = false;
		let currentIndex = 0;

		const schedule = (callback: () => void, delay: number) => {
			const timeoutId = window.setTimeout(() => {
				timeouts.delete(timeoutId);
				callback();
			}, delay);
			timeouts.add(timeoutId);
		};

		const ensureTextNode = (el: HTMLSpanElement) => {
			const textNode = Array.from(el.childNodes).find(
				(node): node is Text => node.nodeType === Node.TEXT_NODE,
			);
			if (textNode) return textNode;
			const nextNode = el.querySelector("[data-hero-cursor]");
			const createdNode = document.createTextNode("");
			if (nextNode) {
				el.insertBefore(createdNode, nextNode);
				return createdNode;
			}
			el.appendChild(createdNode);
			return createdNode;
		};

		const addCursor = (el: HTMLSpanElement) => {
			const existingCursor = el.querySelector<HTMLSpanElement>("[data-hero-cursor]");
			if (existingCursor) return existingCursor;
			const cursor = document.createElement("span");
			cursor.dataset.heroCursor = "true";
			cursor.className = "hero-type-cursor";
			el.appendChild(cursor);
			return cursor;
		};

		const clearLine = (el: HTMLSpanElement) => {
			addCursor(el);
			const textNode = ensureTextNode(el);
			textNode.textContent = "";
		};

		const setLineText = (el: HTMLSpanElement, text: string) => {
			addCursor(el);
			const textNode = ensureTextNode(el);
			textNode.textContent = text;
		};

		const typeLine = (el: HTMLSpanElement, text: string, speed: number, onDone: () => void) => {
			clearLine(el);
			const textNode = ensureTextNode(el);
			let charIndex = 0;
			const tick = () => {
				if (cancelled) return;
				if (charIndex >= text.length) { schedule(onDone, 260); return; }
				charIndex += 1;
				textNode.textContent = text.slice(0, charIndex);
				schedule(tick, speed);
			};
			schedule(tick, speed);
		};

		const eraseLine = (el: HTMLSpanElement, speed: number, onDone: () => void) => {
			const textNode = ensureTextNode(el);
			const tick = () => {
				if (cancelled) return;
				const currentText = textNode.textContent ?? "";
				if (currentText.length === 0) { schedule(onDone, 80); return; }
				textNode.textContent = currentText.slice(0, -1);
				schedule(tick, speed);
			};
			schedule(tick, speed);
		};

		const typePhrase = (phraseIndex: number) => {
			if (cancelled) return;
			const phrase = heroPhrases[phraseIndex];
			setPhraseIndex(phraseIndex);
			typeLine(assetEl, phrase.assetLine, TYPE_SPEED_MS, () => {
				typeLine(leverageEl, phrase.leverageLine, TYPE_SPEED_MS, () => {
					schedule(() => {
						eraseLine(leverageEl, BACK_SPEED_MS, () => {
							eraseLine(assetEl, BACK_SPEED_MS, () => {
								currentIndex = (currentIndex + 1) % heroPhrases.length;
								schedule(() => typePhrase(currentIndex), SWAP_DELAY_MS);
							});
						});
					}, BACK_DELAY_MS);
				});
			});
		};

		setLineText(assetEl, heroPhrases[0].assetLine);
		setLineText(leverageEl, heroPhrases[0].leverageLine);
		schedule(() => {
			eraseLine(leverageEl, BACK_SPEED_MS, () => {
				eraseLine(assetEl, BACK_SPEED_MS, () => {
					currentIndex = (currentIndex + 1) % heroPhrases.length;
					schedule(() => typePhrase(currentIndex), SWAP_DELAY_MS);
				});
			});
		}, START_DELAY_MS);

		return () => {
			cancelled = true;
			timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
		};
	}, [heroPhrases]);

	return (
		<section className={cn(sectionClass, "relative overflow-hidden")}>
			<div
				className="pointer-events-none absolute inset-0 opacity-90"
				style={{
					background:
						"radial-gradient(circle at 10% 14%, color-mix(in oklab, var(--primary) 18%, transparent), transparent 24%), radial-gradient(circle at 86% 10%, color-mix(in oklab, var(--primary) 15%, transparent), transparent 28%), linear-gradient(180deg, transparent 0%, rgb(0 0 0 / 0.08) 100%)",
				}}
			/>

			<motion.div
				className="relative grid gap-8 px-[18px] py-[34px] md:px-[42px] md:py-[54px] lg:grid-cols-[minmax(0,1.2fr)_minmax(300px,0.8fr)] lg:items-center lg:gap-10"
				initial="hidden"
				animate="visible"
				transition={{ staggerChildren: 0.12, delayChildren: 0.15 }}
			>
				<div>
					<motion.div
						className="inline-flex items-center gap-2 rounded-full border border-primary/18 bg-primary/10 px-4 py-2 text-label2-bold text-primary"
						variants={fadeBlurUp}
						transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
					>
						<span className="h-2 w-2 rounded-full bg-primary" />
						{t("hero.badge")}
					</motion.div>

					<motion.div
						className="relative mt-4 max-w-[38rem]"
						variants={fadeBlurUp}
						transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
					>
						<p className="invisible text-body2 text-foreground/68">
							{longestEyebrow}
						</p>
						<p className="absolute inset-0 text-body2 text-foreground/68">
							{activePhrase.eyebrow}
						</p>
					</motion.div>

					<motion.h1
						className="mt-5 text-display3 text-foreground sm:text-hero"
						variants={fadeBlurUp}
						transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
					>
						<span className="relative block">
							<span className="invisible block whitespace-nowrap">
								{longestAssetLine}
							</span>
							<span className="relative mt-[0.04em] block">
								<span className="invisible block whitespace-nowrap text-primary">
									{longestLeverageLine}
								</span>
								<span
									ref={leverageTypedRef}
									className="absolute inset-0 block whitespace-nowrap text-primary"
								>
									{heroPhrases[0].leverageLine}
								</span>
							</span>
							<span
								ref={assetTypedRef}
								className="absolute inset-x-0 top-0 block whitespace-nowrap"
							>
								{heroPhrases[0].assetLine}
							</span>
						</span>
						<span className="mt-[0.04em] block">{t("hero.heading-fixed")}</span>
					</motion.h1>

					<motion.p
						className="mt-5 max-w-[640px] text-headline-medium text-muted-foreground md:text-title2-medium"
						variants={fadeBlurUp}
						transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
					>
						{t("hero.description")}
					</motion.p>

					<motion.div
						className="mt-7 flex flex-wrap gap-3"
						variants={fadeBlurUp}
						transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
					>
						<LandingPrimaryCta label={t("hero.cta-primary")} location="hero" />
					</motion.div>

					<motion.div
						className="mt-8 grid gap-3 sm:grid-cols-3"
						variants={fadeBlurUp}
						transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
					>
						{heroStats.map((stat) => (
							<div
								key={stat.label}
								className={cn(
									"rounded-[18px] border px-4 py-4",
									stat.highlight
										? "border-primary/20 bg-primary/8 shadow-[0_18px_40px_rgb(0_0_0_/_0.22)]"
										: "border-[var(--color-theme-static-white)]/8 bg-[var(--color-theme-static-white)]/4 shadow-[0_18px_40px_rgb(0_0_0_/_0.18)]",
								)}
							>
								<div className={cn(
									"text-title1",
									stat.highlight ? "text-primary" : "text-foreground",
								)}>
									{stat.highlight
										? <CountUp display={stat.value} />
										: stat.value}
								</div>
								<div className="mt-1 text-label2 text-muted-foreground">
									{stat.label}
								</div>
							</div>
						))}
					</motion.div>

				</div>

				<motion.div
					className="relative hidden sm:block"
					variants={scaleIn}
					transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
				>
					<div className="absolute inset-x-[12%] top-[-6%] h-28 rounded-full bg-primary/18 blur-3xl" />
					<div className="relative overflow-hidden rounded-[28px] border border-[var(--color-theme-static-white)]/10 bg-[var(--color-theme-bg-elevated)] p-5 shadow-[0_28px_90px_rgb(0_0_0_/_0.34)]">
						<div className="flex items-center justify-between border-b border-[var(--color-theme-static-white)]/8 pb-4">
							<div>
								<div className="text-label2 uppercase text-primary/85">
									{t("hero.board-label")}
								</div>
								<div className="mt-1 flex items-center gap-2 text-title2 text-foreground">
									<CandlestickChart className="h-4 w-4 text-primary" />
									{t("hero.board-title")}
								</div>
							</div>
							<div className="flex items-center gap-1.5 rounded-full border border-[var(--color-theme-static-white)]/10 bg-[var(--color-theme-static-white)]/6 px-3 py-1.5 text-label2 text-muted-foreground">
								<span className="h-1.5 w-1.5 rounded-full bg-[var(--color-theme-primary-normal)] animate-pulse" />
								{t("hero.board-live")}
							</div>
						</div>

						<div className="mt-4 space-y-3">
							{boardMarkets.map((market, i) => {
								const positive = market.priceChangePercent >= 0;
								const isFirst = i === 0;
								return (
									<Link
										key={market.routeSymbol}
										to="/trade/$symbol"
										params={{ symbol: market.routeSymbol }}
										className={cn(
											"group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-[18px] border px-4 py-3 transition hover:-translate-y-0.5 hover:border-primary/24 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
											isFirst
												? "border-primary/16 bg-primary/6"
												: "border-[var(--color-theme-static-white)]/8 bg-[var(--color-theme-static-white)]/4",
										)}
									>
											<div className="min-w-0">
												<div className="truncate text-headline text-foreground">
													{getLocalizedMarketName({
														coin: market.coin,
														dex: market.dex,
														t,
													})}
												</div>
												<div className="mt-1 text-label2 text-muted-foreground">
													{market.displaySymbol}
											</div>
										</div>
										<div className="text-right">
											<div className="relative overflow-hidden text-headline text-foreground">
												<AnimatePresence mode="popLayout">
													<motion.span
														key={formatPrice(market.markPrice)}
														initial={{ y: 10, opacity: 0 }}
														animate={{ y: 0, opacity: 1 }}
														exit={{ y: -10, opacity: 0 }}
														transition={{ duration: 0.3, ease: "easeOut" }}
														className="inline-block"
													>
														${formatPrice(market.markPrice)}
													</motion.span>
												</AnimatePresence>
											</div>
											<div
												className={cn(
													"mt-1 text-label2-medium",
													positive ? "text-trade-green" : "text-trade-red",
												)}
											>
												{formatSignedPercent(market.priceChangePercent)}
											</div>
										</div>
									</Link>
								);
							})}
						</div>
					</div>
				</motion.div>
			</motion.div>
		</section>
	);
}
